import { todayMx } from '../lib/dateUtils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { Plus, Search, MoreHorizontal, Edit, Trash2, Loader2, FileDown, Send, Eye, X, TrendingUp, CheckCircle } from 'lucide-react';

const estatusConfig = {
  pendiente:      { label: 'Pendiente',      bg: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  en_seguimiento: { label: 'En seguimiento', bg: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  aceptado:       { label: 'Aceptado',       bg: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  rechazado:      { label: 'Rechazado',      bg: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  vencido:        { label: 'Vencido',        bg: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400' },
};
const serviceTypes = ['Sondeo de drenaje','Plomería','Electricidad','Aire Acondicionado','Detección de fugas','Mantenimiento general','Venta de minisplit','Otro'];

const ivaOptions = [
  { value: 'sin_iva', label: 'Sin IVA', rate: 0 },
  { value: 'iva_8', label: 'IVA 8%', rate: 0.08 },
  { value: 'iva_16', label: 'IVA 16%', rate: 0.16 },
];
const ivaRate = (tipo) => (ivaOptions.find(o => o.value === tipo)?.rate ?? 0);

function getEmptyForm() {
  return { fecha: todayMx(), nombre: '', telefono: '', direccion: '', tipo_servicio: '', descripcion: '', monto_cotizado: '', anticipo: '', iva_tipo: 'sin_iva', observaciones: '', tecnico_asignado: '', estatus: 'pendiente', vigencia_dias: 15 };
}

export default function PresupuestosV2() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterEstatus, setFilterEstatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [form, setForm] = useState(getEmptyForm());
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filterEstatus !== 'all') params.append('estatus', filterEstatus);
      const [res, statsRes, techRes] = await Promise.all([
        axios.get(`${API}/presupuestos-v2?${params}`),
        axios.get(`${API}/presupuestos-v2/stats`),
        axios.get(`${API}/technicians`)
      ]);
      setItems(res.data);
      setStats(statsRes.data);
      setTechnicians(techRes.data.filter(t => t.is_active !== false));
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, [search, filterEstatus]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre) { toast.error('Nombre obligatorio'); return; }
    setSaving(true);
    try {
      if (selectedItem) {
        await axios.put(`${API}/presupuestos-v2/${selectedItem.id}`, form);
        toast.success('Presupuesto actualizado');
      } else {
        await axios.post(`${API}/presupuestos-v2`, form);
        toast.success('Presupuesto creado');
      }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar presupuesto ${item.folio}?`)) return;
    try { await axios.delete(`${API}/presupuestos-v2/${item.id}`); toast.success('Eliminado'); fetchData(); }
    catch { toast.error('Error'); }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await axios.put(`${API}/presupuestos-v2/${item.id}`, { estatus: newStatus });
      if (newStatus === 'aceptado') toast.success(`${item.folio} aceptado — Cliente y servicio creados automáticamente`);
      else toast.success('Estatus actualizado');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDownloadPDF = async (item) => {
    try {
      const res = await axios.get(`${API}/presupuestos-v2/${item.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Presupuesto_${item.folio}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Error al descargar PDF'); }
  };

  const handleWhatsApp = (item) => {
    const msg = encodeURIComponent(`Hola ${item.nombre}, le envío el presupuesto ${item.folio} de REPACS.\n\nServicio: ${item.tipo_servicio}\nTotal: $${(item.total||item.monto_cotizado||0).toLocaleString()}\n\nQuedo a sus órdenes.`);
    const phone = (item.telefono || '').replace(/\D/g, '');
    window.open(`https://wa.me/${phone.length === 10 ? '52' + phone : phone}?text=${msg}`, '_blank');
  };

  const openNew = () => { setSelectedItem(null); setForm(getEmptyForm()); setDialogOpen(true); };
  const openEdit = (item) => {
    setSelectedItem(item);
    setForm({ fecha: item.fecha||'', nombre: item.nombre||'', telefono: item.telefono||'', direccion: item.direccion||'', tipo_servicio: item.tipo_servicio||'', descripcion: item.descripcion||'', monto_cotizado: item.monto_cotizado||'', anticipo: item.anticipo||'', iva_tipo: item.iva_tipo||'sin_iva', observaciones: item.observaciones||'', tecnico_asignado: item.tecnico_asignado||'', estatus: item.estatus||'pendiente', vigencia_dias: item.vigencia_dias||15 });
    setDialogOpen(true);
  };

  if (loading) return <div className="flex items-center justify-center h-96"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="presupuestos-page">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Presupuestos</h2>
          <p className="text-muted-foreground text-sm">{stats.total || 0} cotizaciones · {stats.conversion || 0}% conversión</p>
        </div>
        <Button onClick={openNew} data-testid="new-presupuesto-btn"><Plus className="h-4 w-4 mr-2" />Nuevo Presupuesto</Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {[
          { label: 'Total', val: stats.total||0, dot: 'bg-slate-500' },
          { label: 'Pendientes', val: stats.pendientes||0, dot: 'bg-amber-500' },
          { label: 'Seguimiento', val: stats.en_seguimiento||0, dot: 'bg-blue-500' },
          { label: 'Aceptados', val: stats.aceptados||0, dot: 'bg-green-500' },
          { label: 'Rechazados', val: stats.rechazados||0, dot: 'bg-red-500' },
          { label: 'Conversión', val: `${stats.conversion||0}%`, dot: 'bg-purple-500' },
        ].map(c => (
          <Card key={c.label}><CardContent className="p-2.5 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1"><div className={`w-2 h-2 rounded-full ${c.dot}`} /><span className="text-xs text-muted-foreground">{c.label}</span></div>
            <span className="text-xl font-bold">{c.val}</span>
          </CardContent></Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar por nombre, teléfono, dirección o folio..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        {['all','pendiente','en_seguimiento','aceptado','rechazado','vencido'].map(s => (
          <Button key={s} variant={filterEstatus === s ? 'default' : 'outline'} size="sm" className="rounded-full" onClick={() => setFilterEstatus(s)}>
            {s === 'all' ? 'Todos' : (estatusConfig[s]?.label || s)}
          </Button>
        ))}
      </div>

      {/* Table */}
      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-[90px]">Folio</TableHead>
            <TableHead className="w-[80px]">Fecha</TableHead>
            <TableHead>Nombre</TableHead>
            <TableHead className="w-[100px]">Teléfono</TableHead>
            <TableHead className="w-[100px]">Servicio</TableHead>
            <TableHead className="w-[90px] text-right">Total</TableHead>
            <TableHead className="w-[80px] text-right">Anticipo</TableHead>
            <TableHead className="w-[90px]">Técnico</TableHead>
            <TableHead className="w-[110px]">Estatus</TableHead>
            <TableHead className="w-[50px]">Acc.</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No hay presupuestos</TableCell></TableRow>
            ) : items.map(item => {
              const est = estatusConfig[item.estatus] || estatusConfig.pendiente;
              return (
                <TableRow key={item.id} className={item.estatus === 'aceptado' ? 'bg-green-50/30 dark:bg-green-950/10' : item.estatus === 'rechazado' || item.estatus === 'vencido' ? 'opacity-60' : ''}>
                  <TableCell className="font-mono text-xs font-bold text-blue-600">{item.folio}</TableCell>
                  <TableCell className="text-sm">{item.fecha ? format(parseISO(item.fecha), 'dd/MM/yy') : '-'}</TableCell>
                  <TableCell className="font-medium text-sm">{item.nombre}</TableCell>
                  <TableCell className="text-xs">{item.telefono || '-'}</TableCell>
                  <TableCell className="text-xs">{item.tipo_servicio || '-'}</TableCell>
                  <TableCell className="text-right font-medium">${(item.total||item.monto_cotizado||0).toLocaleString()}</TableCell>
                  <TableCell className="text-right text-sm">${(item.anticipo||0).toLocaleString()}</TableCell>
                  <TableCell className="text-xs">{item.tecnico_asignado?.split(' ')[0] || '-'}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-auto p-0"><Badge className={`${est.bg} cursor-pointer`}>{est.label}</Badge></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        {Object.entries(estatusConfig).map(([k, v]) => (
                          <DropdownMenuItem key={k} onClick={() => handleStatusChange(item, k)}>
                            <Badge className={`${v.bg} mr-2`}>.</Badge>{v.label}
                            {k === 'aceptado' && <span className="text-xs text-green-600 ml-2">(crea cliente)</span>}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setSelectedItem(item); setDetailOpen(true); }}><Eye className="h-4 w-4 mr-2" />Ver Detalle</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(item)}><Edit className="h-4 w-4 mr-2" />Editar</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDownloadPDF(item)}><FileDown className="h-4 w-4 mr-2" />Descargar PDF</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleWhatsApp(item)}><Send className="h-4 w-4 mr-2" />Enviar WhatsApp</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleDelete(item)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" />Eliminar</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent></Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selectedItem ? 'Editar' : 'Nuevo'} Presupuesto</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({...form, fecha: e.target.value})} /></div>
              <div className="space-y-2"><Label>Vigencia (días)</Label><Input type="number" value={form.vigencia_dias} onChange={(e) => setForm({...form, vigencia_dias: parseInt(e.target.value)||15})} min="1" /></div>
            </div>
            <div className="space-y-2"><Label>Nombre *</Label><Input value={form.nombre} onChange={(e) => setForm({...form, nombre: e.target.value})} required data-testid="pres-nombre" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Teléfono</Label><Input value={form.telefono} onChange={(e) => setForm({...form, telefono: e.target.value})} /></div>
              <div className="space-y-2"><Label>Tipo de Servicio</Label>
                <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={form.tipo_servicio} onChange={(e) => setForm({...form, tipo_servicio: e.target.value})}>
                  <option value="">Seleccionar...</option>
                  {serviceTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="space-y-2"><Label>Dirección</Label><Input value={form.direccion} onChange={(e) => setForm({...form, direccion: e.target.value})} /></div>
            <div className="space-y-2"><Label>Descripción del Trabajo</Label><Textarea value={form.descripcion} onChange={(e) => setForm({...form, descripcion: e.target.value})} rows={3} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Subtotal (Monto Cotizado)</Label><Input data-testid="pres-monto" type="number" value={form.monto_cotizado} onChange={(e) => setForm({...form, monto_cotizado: e.target.value})} placeholder="$0" /></div>
              <div className="space-y-2"><Label>Tipo de cotización</Label>
                <Select value={form.iva_tipo} onValueChange={(v) => setForm({...form, iva_tipo: v})}>
                  <SelectTrigger data-testid="pres-iva-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ivaOptions.map(o => <SelectItem key={o.value} value={o.value} data-testid={`pres-iva-${o.value}`}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(() => {
              const sub = parseFloat(form.monto_cotizado) || 0;
              const ant = parseFloat(form.anticipo) || 0;
              const rate = ivaRate(form.iva_tipo);
              const ivaMonto = +(sub * rate).toFixed(2);
              const total = +(sub + ivaMonto).toFixed(2);
              const saldo = +(total - ant).toFixed(2);
              return (
                <div className="rounded-lg border bg-muted/30 p-3 space-y-1 text-sm" data-testid="pres-breakdown">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-medium">${sub.toLocaleString('es-MX',{minimumFractionDigits:2})}</span></div>
                  {rate > 0 && (
                    <div className="flex justify-between"><span className="text-muted-foreground">{ivaOptions.find(o => o.value === form.iva_tipo)?.label || 'IVA'}</span><span className="font-medium">${ivaMonto.toLocaleString('es-MX',{minimumFractionDigits:2})}</span></div>
                  )}
                  <div className="flex justify-between border-t pt-1"><span className="font-semibold">Total</span><span className="font-bold text-blue-700 dark:text-blue-400">${total.toLocaleString('es-MX',{minimumFractionDigits:2})}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Anticipo</span><span className="text-green-600">${ant.toLocaleString('es-MX',{minimumFractionDigits:2})}</span></div>
                  <div className="flex justify-between"><span className="font-semibold">Saldo pendiente</span><span className="font-bold text-orange-600">${saldo.toLocaleString('es-MX',{minimumFractionDigits:2})}</span></div>
                </div>
              );
            })()}
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2"><Label>Anticipo</Label><Input data-testid="pres-anticipo" type="number" value={form.anticipo} onChange={(e) => setForm({...form, anticipo: e.target.value})} placeholder="$0" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Técnico Asignado</Label>
                <select className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" value={form.tecnico_asignado} onChange={(e) => setForm({...form, tecnico_asignado: e.target.value})}>
                  <option value="">Seleccionar...</option>
                  {technicians.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
                </select>
              </div>
              <div className="space-y-2"><Label>Estatus</Label>
                <Select value={form.estatus} onValueChange={(v) => setForm({...form, estatus: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(estatusConfig).map(([k,v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2"><Label>Observaciones</Label><Textarea value={form.observaciones} onChange={(e) => setForm({...form, observaciones: e.target.value})} rows={2} /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Presupuesto {selectedItem?.folio}</DialogTitle></DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <Badge className={`${(estatusConfig[selectedItem.estatus]||estatusConfig.pendiente).bg}`}>{(estatusConfig[selectedItem.estatus]||estatusConfig.pendiente).label}</Badge>
                <span className="text-sm text-muted-foreground">{selectedItem.fecha}</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><p className="text-xs text-muted-foreground">Nombre</p><p className="font-medium">{selectedItem.nombre}</p></div>
                <div><p className="text-xs text-muted-foreground">Teléfono</p><p>{selectedItem.telefono||'-'}</p></div>
                <div className="col-span-2"><p className="text-xs text-muted-foreground">Dirección</p><p>{selectedItem.direccion||'-'}</p></div>
                <div><p className="text-xs text-muted-foreground">Servicio</p><p>{selectedItem.tipo_servicio||'-'}</p></div>
                <div><p className="text-xs text-muted-foreground">Técnico</p><p>{selectedItem.tecnico_asignado||'-'}</p></div>
              </div>
              {selectedItem.descripcion && <div className="p-3 bg-muted/50 rounded-lg"><p className="text-xs text-muted-foreground">Descripción</p><p className="text-sm mt-1">{selectedItem.descripcion}</p></div>}
              <div className="rounded-lg border bg-blue-50 dark:bg-blue-950/20 p-4 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-medium">${(selectedItem.monto_cotizado||0).toLocaleString('es-MX',{minimumFractionDigits:2})}</span></div>
                {(selectedItem.iva_tipo && selectedItem.iva_tipo !== 'sin_iva') && (
                  <div className="flex justify-between"><span className="text-muted-foreground">{ivaOptions.find(o => o.value === selectedItem.iva_tipo)?.label || 'IVA'}</span><span className="font-medium">${(selectedItem.iva_monto||0).toLocaleString('es-MX',{minimumFractionDigits:2})}</span></div>
                )}
                <div className="flex justify-between border-t pt-1"><span className="font-semibold">Total</span><span className="font-bold text-lg text-blue-700 dark:text-blue-400">${(selectedItem.total || selectedItem.monto_cotizado || 0).toLocaleString('es-MX',{minimumFractionDigits:2})}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Anticipo</span><span className="font-medium text-green-600">${(selectedItem.anticipo||0).toLocaleString('es-MX',{minimumFractionDigits:2})}</span></div>
                <div className="flex justify-between"><span className="font-semibold">Saldo pendiente</span><span className="font-bold text-orange-600">${(selectedItem.saldo_pendiente||0).toLocaleString('es-MX',{minimumFractionDigits:2})}</span></div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleDownloadPDF(selectedItem)}><FileDown className="h-4 w-4 mr-1" />PDF</Button>
                <Button size="sm" variant="outline" onClick={() => handleWhatsApp(selectedItem)}><Send className="h-4 w-4 mr-1" />WhatsApp</Button>
                {selectedItem.estatus !== 'aceptado' && (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => { handleStatusChange(selectedItem, 'aceptado'); setDetailOpen(false); }}><CheckCircle className="h-4 w-4 mr-1" />Aceptar</Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
